import { Info as InfoIcon } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { isNil, toNumber } from "lodash";
import type { FC } from "react";
import { useEffect, useState } from "react";
import PriceListFromOtherOption from "../../admin/products/editProduct/PriceListFromOtherOptionAlert";
import type { PriceListPriceValue } from "../../admin/products/editProduct/updateOptionPrices";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledCheckbox from "../../components/labelledCheckbox/LabelledCheckbox";
import type { ID } from "../../data/BaseEntity";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import { useProductOptionPrices } from "./useProductOptionPrices";

const DynamicPricesAlert = () => {
  return (
    <Alert icon={<InfoIcon fontSize="inherit" />} severity="info" sx={{ marginBottom: 1 }}>
      Edycja cen niedostępna. Wykorzystywane są ceny dynamiczne
    </Alert>
  );
};

function getPriceForDate(prices: Record<string, PriceListPriceValue> | undefined, date: string): PriceListPriceValue {
  const priceForDate = prices?.[date];
  return isNil(priceForDate) ? "ON_DEMAND" : priceForDate;
}

const modes = [
  { value: "NUMBER", label: "Cena" },
  { value: "ON_DEMAND", label: "Na zapytanie" },
  { value: "UNAVAILABLE", label: "Niedostępny" },
] as const;

interface Props {
  readonly productId: ID;
  readonly date: string;
  readonly option: { id: string; price?: PriceValue };
  readonly onClose: () => void;
  readonly onChange: (value: PriceValue) => void;
}

const OptionPriceEditDialog: FC<Props> = ({ productId, date, option, onClose, onChange }) => {
  const [value, setValue] = useState(Price.fromNullable(option.price).value);
  const [updatePriceList, setUpdatePriceList] = useState(false);
  const {
    isLoading,
    updatePriceListPrice,
    currency: productCurrency,
    priceListPrices,
    usePricesFrom,
    useDynamicPrices,
    computePricesFromPurchasePrice,
  } = useProductOptionPrices(productId, option.id);

  useEffect(() => {
    if (priceListPrices && !Price.fromNullable(value).isPresent()) {
      const priceForDate = getPriceForDate(priceListPrices, date);
      // Default to NUMBER mode (0) when selling price is also ON_DEMAND so the user can type directly
      setValue(Price.fromNullable(priceForDate).isPresent() ? priceForDate : 0);
    }
  }, [priceListPrices]);

  const priceListPrice = getPriceForDate(priceListPrices, date);
  const priceListPriceDifferent = !isNil(option.price) && !isNil(priceListPrice) && option.price !== priceListPrice;
  const price = Price.fromNullable(option.price ?? priceListPrice).value;

  const handleUpdate = async () => {
    if (value !== "INVALID") {
      if (updatePriceList) {
        await updatePriceListPrice(date, value);
      }
      onChange(value);
    }
  };

  const mode = typeof value === "number" ? "NUMBER" : value;
  const arePricesSame = Price.fromNullable(price).equals(Price.fromNullable(value));
  const editDisabled = !!usePricesFrom || useDynamicPrices;
  // When the price is computed from purchase price with margin, the value edited here is a target selling
  // price, which cannot be reversed into a purchase price. The price list may only be updated to ON_DEMAND
  // or UNAVAILABLE in that case.
  const priceListUpdateForbidden = !!computePricesFromPurchasePrice && mode === "NUMBER";

  useEffect(() => {
    if (priceListUpdateForbidden) {
      setUpdatePriceList(false);
    }
  }, [priceListUpdateForbidden]);

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Modyfikuj cenę opcji</DialogTitle>
      <DialogContent>
        {isLoading && <LinearProgress data-testid="loading-indicator" />}
        {usePricesFrom && (
          <PriceListFromOtherOption productId={usePricesFrom.productId} optionId={usePricesFrom.optionId} />
        )}
        {useDynamicPrices && <DynamicPricesAlert />}
        <ToggleButtonGroup
          value={mode}
          disabled={editDisabled}
          exclusive
          onChange={(_, v) => {
            if (v !== null) {
              if (v === "NUMBER") {
                setValue(0);
              } else {
                setValue(v);
              }
            }
          }}
          aria-label="text alignment"
        >
          {modes.map((m) => (
            <ToggleButton key={m.value} value={m.value}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          label="Cena"
          margin="dense"
          type={mode === "NUMBER" ? "number" : undefined}
          fullWidth
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">{productCurrency}</InputAdornment>,
              readOnly: mode !== "NUMBER",
            },
          }}
          value={mode === "NUMBER" ? value : modes.find((m) => m.value === mode)?.label}
          onChange={(e) => setValue(toNumber(e.target.value))}
          disabled={editDisabled}
        />
        {priceListPriceDifferent && (
          <Typography variant="body2">
            Cena w cenniku: {Price.fromNullable(priceListPrice).toDisplayValue(productCurrency)}
          </Typography>
        )}
        <LabelledCheckbox
          label="Aktualizuj cennik"
          checked={updatePriceList}
          disabled={
            Price.fromNullable(value).equals(Price.fromNullable(priceListPrice)) ||
            editDisabled ||
            priceListUpdateForbidden
          }
          onChange={setUpdatePriceList}
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Anuluj
        </Button>
        <Button
          disabled={!priceListPriceDifferent || editDisabled}
          color="error"
          onClick={() => onChange(priceListPrice)}
        >
          Przywróć domyślną
        </Button>
        <AsyncLoadingButton
          color="primary"
          disabled={arePricesSame || editDisabled || value === "INVALID"}
          onClick={handleUpdate}
        >
          Zmień
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default OptionPriceEditDialog;
